import React, { useCallback, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import Image from '@atlaskit/image';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { onAIAnswerTabClick } from '../../../ai-answer-dialog/analytics';
import { useAnalytics } from '../../../common/controller/analytics/use-analytics';
import { AIAnswerTab, aiAnswerTabsOrder } from '../../../constants';
import { AIDisclaimerAndFeedback } from '../../common/ui/ai-disclaimer-and-feedback';
import { AITabs } from '../../common/ui/ai-tabs';
import { AITabsHeader } from '../../common/ui/ai-tabs-header';
import { AITabsPanel } from '../../common/ui/ai-tabs-panel';
import { AITabsSources } from '../../common/ui/ai-tabs-sources';
import CalendarDark from '../assets/calendar-dark.svg';
import CalendarLight from '../assets/calendar-light.svg';
import { StandardActionsKnowledgeCard } from '../standard-cards/StandardActionsKnowledgeCard';

const messages = defineMessages({
	knowledge_card_date_category: {
		id: 'search_ai_knowledge_card_date_category',
		defaultMessage: 'Date',
		description: 'Category header for date knowledge card',
	},
});

const styles = cssMap({
	fullHeight: {
		height: '100%',
	},
	containerStyles: {
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingLeft: token('space.0'),
		paddingBottom: token('space.0'),
	},
	disclaimer: {
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingTop: token('space.050'),
	},
});

export const DateCard = ({ date }: { date: string }): React.JSX.Element => {
	const [selectedTab, setSelectedTab] = useState<AIAnswerTab>(AIAnswerTab.AI_ANSWER);

	const { formatMessage } = useIntl();
	const [fireAnalyticsEvent] = useAnalytics();
	const { commonAttributes } = useAIAnswerContext();
	const { sources } = commonAttributes;

	const handleTabChange = useCallback(
		(selectedTabIndex: number) => {
			const selectedTab = aiAnswerTabsOrder[selectedTabIndex];
			setSelectedTab(selectedTab);

			fireAnalyticsEvent(
				onAIAnswerTabClick({
					...commonAttributes,
					selectedTab,
				}),
			);
		},
		[setSelectedTab, fireAnalyticsEvent, commonAttributes],
	);

	return (
		<Stack alignBlock="stretch" grow="fill" spread="space-between" xcss={styles.fullHeight}>
			<AITabs onChange={handleTabChange} selectedTabIndex={aiAnswerTabsOrder.indexOf(selectedTab)}>
				<AITabsHeader showSourcesTab={sources.length > 0} />
				<AITabsPanel>
					<StandardActionsKnowledgeCard
						name="smart-answer-date-knowledge-card"
						category={formatMessage(messages.knowledge_card_date_category)}
						primaryDescription={date}
						media={<Image src={CalendarLight} srcDark={CalendarDark} alt="" />}
						size="full"
						containerStyles={styles.containerStyles}
					/>
				</AITabsPanel>
				{sources.length > 0 ? (
					<AITabsPanel>
						<AITabsSources />
					</AITabsPanel>
				) : null}
			</AITabs>
			<Box xcss={styles.disclaimer}>
				<AIDisclaimerAndFeedback showFeedbackButtons={true} showCopyButton copyText={date} />
			</Box>
		</Stack>
	);
};
